import React, { Component } from 'react'
import prepareComponent from 'client/shared/decorators/prepare-component'
import { withI18n, withI18nProps } from '@lingui/react'
import { connect } from 'react-redux'
import compose from 'lodash/fp/compose'
import isEmpty from 'lodash/isEmpty'
import last from 'lodash/last'

import urlFor from 'shared/tools/url-helper'
import { authorsAsString } from 'client/bookmate/helpers/author/authorsAsString'

import { load, loadVariants } from 'client/bookmate/reducers/series-reducer'
import { getSeries } from 'client/bookmate/selectors/series-selectors'

import MetaTitle from 'client/shared/blocks/meta-title'
import Meta from 'client/shared/blocks/meta'
import { MetaDescription } from 'client/shared/blocks/meta-description'
import MetaDeeplink from 'client/shared/blocks/meta-deeplink'
import MetaTwinBox from 'client/shared/boxes/meta-twin-box'

import { SeriesProps } from 'client/shared/types/series'
import { State as AppState } from 'client/shared/reducers/app-reducer'
import { CurrentUserState } from 'client/shared/types/current-user'
import { Route } from 'client/shared/types/react-router'
import { Dispatch, State } from 'shared/types/redux'
import {
  analyticsEvent,
  CONTENT_VIEWED,
} from 'client/shared/reducers/analytics-reducer'
import { getCurrentDateTime } from 'shared/tools/date-helper'

type Props = {
  series: SeriesProps
  children: React.ReactNode
  app: AppState
  currentUser: CurrentUserState
  routes: Route[]
  dispatch: Dispatch
} & withI18nProps

class SeriesPage extends Component<Props> {
  componentDidMount(): void {
    const {
      dispatch,
      currentUser: {
        data: { id },
      },
      series: { uuid },
    } = this.props
    const dateTime = getCurrentDateTime()

    dispatch(
      analyticsEvent(CONTENT_VIEWED, {
        event_datetime: dateTime,
        user_id: id,
        content_type: 'series',
        content_uuid: uuid,
      }),
    )
  }
  render() {
    const { series } = this.props

    if (isEmpty(series)) return null

    return (
      <>
        {this.renderMeta()}
        {this.props.children}
      </>
    )
  }

  renderMeta() {
    const {
      app: { host },
      series,
      routes,
      i18n,
      currentUser: {
        data: { locale },
      },
    } = this.props

    if (!series.uuid) return null

    const title = i18n._('series.head_title', {
      title: series.title,
      authors: authorsAsString(series.authors),
    })

    const messageId =
      series.resourcesTypes[0] === 'audiobook'
        ? 'series.meta_description_audio'
        : 'series.meta_description'
    const description = i18n._(messageId, {
      title: series.title,
      authors: authorsAsString(series.authors),
      annotation: series.description,
    })

    const ogUrl = `https://${host}${urlFor.series(series.uuid)}`
    const ogTitle = `${series.title}, ${authorsAsString(series.authors)}`

    return (
      <>
        <MetaTitle
          title={title}
          og={ogTitle}
          disableSuffix={locale === 'ru'}
          hasOgSuffix
        />
        <MetaTwinBox
          language={series.language}
          {...(last(routes)?.path === 'followers' ? { robotsParams: {} } : {})}
        />
        <MetaDescription description={description} />
        <MetaDeeplink target="series" uuid={series.uuid} />
        <Meta content={ogUrl} property="og:url" />
        <Meta content="false" property="al:web:should_fallback" />
        {this.renderCoverMeta()}
      </>
    )
  }

  renderCoverMeta() {
    const {
      series: { uuid: seriesId },
    } = this.props
    const canvasFacebookLink = urlFor.canvasSeries({
      seriesId,
      mode: 'facebook',
    })
    const canvasTwitterLink = urlFor.canvasSeries({
      seriesId,
      mode: 'twitter',
    })

    return (
      <>
        <Meta content={canvasFacebookLink} property="og:image" />
        <Meta content={canvasTwitterLink} property="twitter:image" />
      </>
    )
  }
}

const prepareWrapper = prepareComponent(
  // eslint-disable-next-line func-names
  async function ({ store, params }) {
    const { uuid } = params
    const { dispatch } = store

    const actions = [dispatch(load(uuid)), dispatch(loadVariants(uuid))]

    await Promise.all(actions)
  },
  {
    updateParams: 'uuid',
    userListener: true,
  },
)

const connectWrapper = connect((state: State) => ({
  app: state.app,
  currentUser: state.currentUser,
  series: getSeries(state),
}))

const wrappers = compose(
  prepareWrapper,
  connectWrapper,
  withI18n({ update: true }),
)

export default wrappers(SeriesPage)
